import React from 'react';
import { CreditCard, Wallet } from 'lucide-react';
import { formatMoney } from '../../utils/format';
import { useTranslation } from '../../i18n';
import { BANKS } from '../../data/bankData';

export function MyCreditsTab({ credits, setRepayingLoan }) {
  const { t } = useTranslation();
  if (credits.length === 0) {
    return (
      <div className="space-y-3">
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-8 text-center">
          <CreditCard className="w-12 h-12 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-500">Aucun crédit en cours</p>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {credits.map((c, idx) => {
        const isFounder = c.origin === 'founder';
        const cBank = (c.bankId && !isFounder) ? BANKS[c.bankId] : null;
        const safeRemainingMonths = Number.isFinite(c.remainingMonths) ? c.remainingMonths : 0;
        const safeDurationMonths = Number.isFinite(c.durationMonths) && c.durationMonths > 0 ? c.durationMonths : 1;
        const progress = ((safeDurationMonths - safeRemainingMonths) / safeDurationMonths) * 100;
        const safeRemainingPrincipal = Number.isFinite(c.remainingPrincipal) ? c.remainingPrincipal : 0;
        const safePrincipal = Number.isFinite(c.principal) ? c.principal : safeRemainingPrincipal;
        const safeMonthlyPayment = Number.isFinite(c.monthlyPayment) ? c.monthlyPayment : 0;
        const safeAnnualRate = Number.isFinite(c.annualRate) ? c.annualRate : 0;
        const borderColor = isFounder ? '#475569' : (cBank ? cBank.accentColor + '40' : '#E5E7EB');
        const labelColor = isFounder ? '#94A3B8' : (cBank?.color || '#1F3864');
        const labelText = isFounder ? 'Crédit fondateur initial' : (cBank ? cBank.name : `Crédit #${idx + 1}`);
        return (
          <div key={c.id} className={`bg-ink-800 rounded-lg border p-4 ${isFounder ? 'opacity-90' : ''}`} style={{ borderColor }} data-testid={`credit-${c.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {cBank && <img src={cBank.advisor.portrait} alt="" className="w-7 h-7 rounded-full object-cover object-top border" style={{ borderColor: cBank.color }} />}
                {isFounder && (
                  <div className="w-7 h-7 rounded-full bg-slate-700 border border-slate-500 flex items-center justify-center">
                    <Wallet className="w-3.5 h-3.5 text-slate-300" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold" style={{ color: labelColor }}>
                      {labelText}
                    </span>
                    {isFounder && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-700/60 text-slate-400 font-medium uppercase tracking-wider" data-testid={`founder-badge-${c.id}`}>
                        Initial
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500">
                    {(safeAnnualRate * 100).toFixed(2)}% — {safeRemainingMonths} {t('months')} {t('months_remaining')}
                    {isFounder && <span className="ml-1 italic"> · accordé à la création de votre société</span>}
                  </p>
                </div>
              </div>
              <p className="text-sm font-bold text-slate-200">{formatMoney(safePrincipal)}</p>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs mb-2">
              <div><span className="text-slate-500">Mensualité</span><p className="font-bold text-danger">{formatMoney(safeMonthlyPayment)}</p></div>
              <div><span className="text-slate-500">Restant dû</span><p className="font-bold text-slate-300">{formatMoney(Math.max(0, safeRemainingPrincipal))}</p></div>
              <div><span className="text-slate-500">Progression</span><p className="font-bold text-foreground">{Math.round(progress)}%</p></div>
            </div>
            <div className="h-2 rounded-full bg-ink-700/40 overflow-hidden mb-3">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${progress}%`, backgroundColor: cBank?.accentColor || '#1F3864' }} />
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setRepayingLoan(c)}
                disabled={safeRemainingPrincipal <= 0}
                className="text-xs px-3 py-1.5 rounded-md font-bold text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 transition-opacity"
                style={{ backgroundColor: cBank?.color || '#1F3864' }}
                data-testid={`early-repay-${c.id}`}
                title={t('early_repay')}
              >
                <Wallet className="w-3 h-3" />
                {t('early_repay')}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
